import { components, operations } from '~/api-types';
import { apiClient } from '@/api/index';
import { FileExportFormat } from '@/types/files';
import { getFileExportResponseType } from '@/utils/file';

export type BudgetDto = components['schemas']['BudgetResponseDto'];

export type CreateBudgetDto = components['schemas']['CreateBudgetBodyDto'];

export type CreateBulkBudgetsDto =
  components['schemas']['CreateBulkBudgetsBodyDto'];

export type RemainingAllocationDto =
  components['schemas']['RemainingAllocationResponseDto'];

export type PaginatedBudgetsDto =
  components['schemas']['PaginatedBudgetsResponseDto'];

export type GetBudgetsListQueryParams =
  operations['GetBudgetsListEndpoint_getBudgetsList']['parameters']['query'];

export type ExportBudgetsQueryParams =
  operations['AdminExportBudgetsEndpoint_handler']['parameters']['query'];

export type BudgetLabelsListDto =
  components['schemas']['BudgetLabelsListResponseDto'];

export enum BudgetStatus {
  Active = 'active',
  Expired = 'expired',
  Pending = 'pending',
}

export enum BudgetType {
  Co_Financed = 'co-financed',
  Fully_Financed = 'fully-financed',
}

export const labelByBudgetType: Record<BudgetType, string> = {
  [BudgetType.Co_Financed]: 'Co-Financed',
  [BudgetType.Fully_Financed]: 'Fully-Financed',
};

export enum BudgetLabel {
  PAID_ADS = 'PAID ADS',
  ADVERTISING = 'ADVERTISING',
  MARKETING = 'MARKETING',
  SALES = 'SALES',
  PRINT = 'PRINT',
}

export const labelByBudgetLabel: Record<BudgetLabel, string> = {
  [BudgetLabel.PAID_ADS]: 'Paid Ads',
  [BudgetLabel.ADVERTISING]: 'Advertising',
  [BudgetLabel.MARKETING]: 'Marketing',
  [BudgetLabel.SALES]: 'Sales',
  [BudgetLabel.PRINT]: 'Store',
};

export const getBudgetLabelDisplay = (label: string): string => {
  const mappedLabel = Object.entries(labelByBudgetLabel).find(
    ([budgetLabel]) => budgetLabel === label,
  )?.[1];

  return mappedLabel ?? label;
};

export const apiGetBudgetsList = async (
  params?: GetBudgetsListQueryParams,
): Promise<PaginatedBudgetsDto> => {
  const { data } = await apiClient.get('/admin/budgets', {
    params,
  });

  return data;
};

export const apiExportBudgets = async (
  exportFormat: FileExportFormat,
  params: ExportBudgetsQueryParams,
): Promise<void> => {
  const { data } = await apiClient.get(
    `/admin/budgets/export/${exportFormat}`,
    {
      params,
      responseType: getFileExportResponseType(exportFormat),
    },
  );

  return data;
};

export const apiCreateBudget = async (
  businessId: number,
  payload: CreateBudgetDto,
): Promise<BudgetDto> => {
  const { data } = await apiClient.post(
    `/admin/businesses/${businessId}/budgets`,
    payload,
  );

  return data;
};

export const apiCreateBulkBudgets = async (
  payload: CreateBulkBudgetsDto,
): Promise<BudgetDto[]> => {
  const { data } = await apiClient.post('/admin/budgets/bulk', payload);
  return data;
};

export const apiGetRemainingAllocation =
  async (): Promise<RemainingAllocationDto> => {
    const { data } = await apiClient.get('/admin/budgets/remaining-allocation');
    return data;
  };

export const apiGetBudgetsLabelsList =
  async (): Promise<BudgetLabelsListDto> => {
    const { data } = await apiClient.get('/admin/budgets/labels');

    return data;
  };
